import { useRef, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Clock, AlertTriangle, CheckCircle } from "lucide-react";

function formatDuration(seconds) {
  if (!seconds) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  if (mb > 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
}

export default function VideoCard({ video, selected, inVault, vaultDestinations, onToggle, delay }) {
  const hasAnimated = useRef(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!hasAnimated.current && cardRef.current) {
      cardRef.current.style.opacity = '0';
      cardRef.current.style.transform = 'translateY(12px)';
      const timer = setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.style.transition = 'opacity 0.35s ease-out, transform 0.35s ease-out';
          cardRef.current.style.opacity = inVault && !selected ? '0.6' : '1';
          cardRef.current.style.transform = 'translateY(0)';
        }
        hasAnimated.current = true;
      }, delay);
      return () => clearTimeout(timer);
    }
  }, []); // Only on mount

  return (
    <div
      ref={cardRef}
      data-testid={`video-card-${video.uri}`}
      className={`ug-card group relative overflow-hidden cursor-pointer ${
        selected ? "ug-card-selected" : ""
      } ${inVault && !selected ? "opacity-60" : ""}`}
      onClick={onToggle}
    >
      {/* Checkbox */}
      <div className="absolute top-2.5 left-2.5 z-10" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          data-testid={`video-checkbox-${video.uri}`}
          checked={selected}
          onCheckedChange={onToggle}
          className="border-[#52525b] data-[state=checked]:bg-[#c6a96f] data-[state=checked]:border-[#c6a96f] data-[state=checked]:text-[#0f1114]"
        />
      </div>

      {/* In Vault badge — shows which destination(s) */}
      {vaultDestinations && vaultDestinations.length > 0 && (
        <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1 bg-[rgba(15,17,20,0.85)] text-[#5fa87a] text-[9px] font-mono tracking-wider px-2 py-1 rounded-md">
          <CheckCircle className="h-3 w-3" />
          {vaultDestinations.includes("dropbox") && vaultDestinations.includes("gdrive")
            ? "BOTH"
            : vaultDestinations.includes("gdrive")
            ? "GDRIVE"
            : "DROPBOX"
          }
        </div>
      )}
      {inVault && (!vaultDestinations || vaultDestinations.length === 0) && (
        <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1 bg-[rgba(15,17,20,0.85)] text-[#5fa87a] text-[9px] font-mono tracking-wider px-2 py-1 rounded-md">
          <CheckCircle className="h-3 w-3" />
          IN VAULT
        </div>
      )}

      {/* Thumbnail */}
      <div className="aspect-video w-full overflow-hidden bg-[#131518] relative">
        {video.thumbnail ? (
          <img
            src={video.thumbnail}
            alt={video.name}
            className="ug-thumb object-cover w-full h-full transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#3f3f46]">
            <Clock className="h-8 w-8" />
          </div>
        )}
        {/* Duration badge */}
        <span className="absolute bottom-2 right-2 bg-[rgba(15,17,20,0.85)] text-[#e4e4e7] text-[10px] font-mono px-1.5 py-0.5 rounded-md tracking-wide">
          {formatDuration(video.duration)}
        </span>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 data-testid={`video-title-${video.uri}`} className="font-medium text-sm text-[#e4e4e7] truncate pr-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {video.name}
        </h3>
        <div className="text-[11px] text-[#71717a] mt-1.5 flex items-center gap-3 font-mono">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDuration(video.duration)}
          </span>
          {video.file_size > 0 && (
            <span>{formatSize(video.file_size)}</span>
          )}
          {video.width > 0 && (
            <span>{video.width}x{video.height}</span>
          )}
        </div>
        {!video.has_download && (
          <div className="flex items-center gap-1 mt-2 text-[11px] text-[#c47272]">
            <AlertTriangle className="h-3 w-3" />
            <span>Requires Vimeo Advanced plan</span>
          </div>
        )}
      </div>
    </div>
  );
}
